import React from 'react';
import { XMarkIcon, UserIcon, PhoneIcon, EnvelopeIcon, IdentificationIcon, TruckIcon } from '@heroicons/react/24/outline';
import { Chauffeur } from '../../types';

interface DriverDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Chauffeur;
}

const DriverDetailsModal: React.FC<DriverDetailsModalProps> = ({
  isOpen,
  onClose,
  driver
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Détails du chauffeur
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {/* Informations personnelles */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
              Informations personnelles
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Nom</p>
                  <p className="text-sm font-medium text-gray-900">{driver.nom}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Prénom</p>
                  <p className="text-sm font-medium text-gray-900">{driver.prenom}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <IdentificationIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Permis de conduire</p>
                <p className="text-sm font-medium text-gray-900">{driver.permis}</p>
              </div>
            </div>
          </div>

          {/* Coordonnées */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
              Coordonnées
            </h4>
            
            <div className="flex items-center space-x-3">
              <PhoneIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Téléphone</p>
                <p className="text-sm font-medium text-gray-900">{driver.telephone || 'Non renseigné'}</p>
              </div>
            </div>

            {driver.email && (
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{driver.email}</p>
                </div>
              </div>
            )}
          </div>

          {/* Véhicules associés */}
          {driver.camions_info && driver.camions_info.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
                Véhicules autorisés
              </h4>
              
              <div className="space-y-2">
                {driver.camions_info.map((vehicle) => (
                  <div key={vehicle.id} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
                    <TruckIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {vehicle.immatriculation}
                      </p>
                      <p className="text-sm text-gray-500">
                        {vehicle.marque} {vehicle.modele}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Statistiques */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
              Statistiques
            </h4>
            
            <div className="flex items-center space-x-3">
              <TruckIcon className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Nombre de livraisons</p>
                <p className="text-sm font-medium text-gray-900">{driver.nombre_livraisons || 0}</p>
              </div>
            </div>
          </div>

          {/* Informations système */}
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900 border-b border-gray-200 pb-2">
              Informations système
            </h4>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">ID</p>
                <p className="font-medium text-gray-900">#{driver.id}</p>
              </div>
              
              <div>
                <p className="text-gray-500">Créé le</p>
                <p className="font-medium text-gray-900">
                  {driver.created_at ? new Date(driver.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bouton de fermeture */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverDetailsModal;
